import { apiClient } from "@/services/apiClient";
import type {
  NotarialRequestEventDto,
  NotarialRequestStatus,
} from "@/services/notaryRequestsService";
import type { UserAddressCreatePayload } from "@/services/userAddressesService";

/** Notary certificate kinds only (`/me/notarial-requests`). Income certificates use accountant requests. */
export type UserNotarialRequestType =
  | "notarial_certificate"
  | "notarial_legal_entity"
  | "notarial_signature"
  | "notarial_address";

export interface UserNotarialRequestDocument {
  id: string;
  documentType: string;
  fileUrl: string;
  originalFilename: string;
  /**
   * Which side of the request sent this.
   *
   * Group and gate on this, never on
   * `documentType !== "notarial_certificate"` — that test cannot tell a file
   * the professional SENT from one this consumer uploaded, and getting it
   * wrong puts a delete button on their notary's draft.
   */
  uploaderRole: "consumer" | "professional";
  contentType: string;
  createdAt: string;
  /**
   * What the consumer said when uploading. Shared by every document of a
   * batch, since one note is written to each. Null when none was written.
   */
  note: string | null;
}

export type UserNotarialRequestDocumentType =
  | "informacion_registral_bps_dgi"
  | "estatuto_otros"
  | "informacion_registral_bps"
  | "informacion_registral_dgi"
  | "estatuto"
  | "comunicacion_bcu"
  | "legal_document_front"
  | "legal_document_back"
  | "passport_document"
  | "otra_documentacion_notarial"
  | "income_special_format_template"
  | "income_dgi_certificate"
  | "income_dgi_form_6906"
  | "income_bps_certificate"
  | "income_tarjeta_rut"
  | "income_professional_caja_certificate"
  | "income_salary_receipt"
  | "income_invoice";

export type IncomeCertificateWorkModality =
  | "dependent"
  | "independent"
  | "professional_independent";

export interface IncomeCertificateDetailsBody {
  periodMonths: 6 | 12 | 24;
  presentingEntity: string;
  /** Specific institution, only collected for the bank / lender categories. */
  presentingInstitution?: string;
  activity: string;
  activityCustom?: string;
  specialFormatRequested: boolean;
  workModality: IncomeCertificateWorkModality;
  bpsNumber?: string;
}

export interface UserNotarialRequestSummary {
  id: string;
  status: NotarialRequestStatus;
  requestType: string;
  /** The original RFQ's title, only set when requestType is 'custom_quote' (a spawned RFQ child). */
  sourceRequestTitle: string | null;
  /** The originating RFQ id, so this child can link back to its quote history. */
  sourceRequestId: string | null;
  /**
   * The checkout this request came from. Present so a row held at
   * `pending_payment_verification` can link back to its bank-transfer page.
   */
  certificateCheckoutId: string | null;
  acceptedByUserId: string | null;
  acceptedBy: {
    firstName: string | null;
    lastName: string | null;
    email: string;
    profileImage: string | null;
    cedula: string | null;
    notaryMatricula: string | null;
  } | null;
  acceptedAt: string | null;
  estimatedCompletionDate: string | null;
  certificateValidityDate: string | null;
  /**
   * A correction the requester asked for after delivery. The request stays
   * "completed" while one is open, so this is served rather than inferred
   * from status.
   */
  correctionRequestedAt: string | null;
  correctionReason: string | null;
  correctionResolvedAt: string | null;
  correctionOpen: boolean;
  createdAt: string;
  updatedAt: string;
  documents: UserNotarialRequestDocument[];
  incomeCertificateDetails?: Record<string, unknown> | null;
  fulfillmentMode?: string;
}

export interface UserRequestPayment {
  amountMinor: number;
  currency: string;
  status: string;
  paidAt: string | null;
}

export interface UserNotarialRequestDetail extends UserNotarialRequestSummary {
  requestedByUserId: string;
  certificatePathChoice: string | null;
  missingInfoMessage: string | null;
  missingDocumentNotes: Record<string, string> | null;
  certificateNotes?: string | null;
  cannotCompleteNotes: string | null;
  payment?: UserRequestPayment | null;
  pickupAddressSnapshot: string | null;
  deliveryAddressId: string | null;
  deliveryAddressSnapshot: string | null;
  deliveryContactPhone: string | null;
  deliveryContactName: string | null;
  deliveryNotes: string | null;
  events: NotarialRequestEventDto[];
}

export function listMyNotarialRequests(): Promise<
  UserNotarialRequestSummary[]
> {
  return apiClient
    .get<UserNotarialRequestSummary[]>("/me/notarial-requests")
    .then((res) => res.data);
}

export function createMyNotarialRequest(params: {
  requestType: string;
  checkoutId?: string;
  companySnapshot?: {
    legalName?: string;
    tradeName?: string;
    rut?: string;
    legalAddress?: string;
    city?: string;
    department?: string;
    postalCode?: string;
    phone?: string;
    email?: string;
    fechaInicio?: string;
    economicActivity?: string;
  };
  fulfillmentMode?: string;
  invoiceIncludeRut?: boolean;
  invoiceRutDigits?: string;
  companyRutUnverified?: boolean;
}): Promise<{
  id: string;
  status: string;
  requestedByUserId: string;
  createdAt: string;
}> {
  return apiClient
    .post<{
      id: string;
      status: string;
      requestedByUserId: string;
      createdAt: string;
    }>("/me/notarial-requests", {
      requestType: params.requestType,
      checkoutId: params.checkoutId,
      companySnapshot: params.companySnapshot,
      fulfillmentMode: params.fulfillmentMode,
      invoiceIncludeRut: params.invoiceIncludeRut,
      invoiceRutDigits: params.invoiceRutDigits,
      ...(params.companyRutUnverified === true && {
        companyRutUnverified: true,
      }),
    })
    .then((res) => res.data);
}

export function uploadMyNotarialRequestDocument(
  requestId: string,
  documentType: UserNotarialRequestDocumentType,
  file: File,
): Promise<UserNotarialRequestDocument> {
  const formData = new FormData();
  formData.append("documentType", documentType);
  formData.append("file", file);
  return apiClient
    .post<UserNotarialRequestDocument>(
      `/me/notarial-requests/${requestId}/documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

/**
 * A document a professional asked for (spec request-document-slots §3c).
 *
 * `documentType` is null exactly when `customLabel` is set — the database
 * enforces that, so a reader picks the label by checking which one is there,
 * never by preferring one and hoping.
 */
export interface RequestDocumentSlot {
  id: string;
  documentType: string | null;
  customLabel: string | null;
  why: string | null;
  status: "pending" | "fulfilled";
  fulfilledAt: string | null;
}

export function getMyNotarialRequestDocumentSlots(
  requestId: string,
): Promise<{ documents: RequestDocumentSlot[] }> {
  return apiClient
    .get<{
      documents: RequestDocumentSlot[];
    }>(`/me/notarial-requests/${requestId}/document-requests`)
    .then((res) => res.data);
}

/**
 * Fill a slot: the file, and the ROW it answers. No `documentType` — the
 * server takes it from the row, which is the whole point of a slot.
 */
export function uploadMyNotarialRequestDocumentForSlot(
  requestId: string,
  documentRequestId: string,
  file: File,
): Promise<UserNotarialRequestDocument> {
  const formData = new FormData();
  formData.append("documentRequestId", documentRequestId);
  formData.append("file", file);
  return apiClient
    .post<UserNotarialRequestDocument>(
      `/me/notarial-requests/${requestId}/documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

export function getMyNotarialRequestDetail(
  id: string,
): Promise<UserNotarialRequestDetail> {
  return apiClient
    .get<UserNotarialRequestDetail>(`/me/notarial-requests/${id}`)
    .then((res) => res.data);
}

export function cancelMyNotarialRequest(id: string): Promise<void> {
  return apiClient.delete(`/me/notarial-requests/${id}`).then(() => undefined);
}

/**
 * Ask for the delivered document to be corrected. Reopens the professional's
 * upload path with no time limit until they answer it.
 */
export function requestMyNotarialRequestCorrection(
  requestId: string,
  reason: string,
): Promise<void> {
  return apiClient
    .post(`/me/notarial-requests/${requestId}/request-correction`, {
      reason: reason.trim(),
    })
    .then(() => undefined);
}

export function resubmitMyNotarialRequest(
  requestId: string,
  notes?: string,
): Promise<void> {
  return apiClient
    .post(`/me/notarial-requests/${requestId}/resubmit`, {
      notes: notes?.trim() ? notes.trim() : undefined,
    })
    .then(() => undefined);
}

export function deleteMyNotarialRequestDocument(
  requestId: string,
  documentId: string,
): Promise<void> {
  return apiClient
    .delete(`/me/notarial-requests/${requestId}/documents/${documentId}`)
    .then(() => undefined);
}

export function fetchMyNotarialRequestDocumentBlob(
  requestId: string,
  documentId: string,
): Promise<Blob> {
  return apiClient
    .get<Blob>(
      `/me/notarial-requests/${encodeURIComponent(requestId)}/documents/${encodeURIComponent(documentId)}/file`,
      { responseType: "blob" },
    )
    .then((res) => res.data);
}

export function submitMyNotarialRequestDeliveryDetails(
  requestId: string,
  body: {
    deliveryAddressId?: string;
    newDeliveryAddress?: UserAddressCreatePayload;
    /** Optional when the saved address already stores a phone. */
    deliveryContactPhone?: string;
    deliveryContactName?: string;
    deliveryNotes?: string;
  },
): Promise<{ id: string; status: string }> {
  return apiClient
    .patch<{
      id: string;
      status: string;
    }>(`/me/notarial-requests/${encodeURIComponent(requestId)}/delivery-details`, body)
    .then((res) => res.data);
}

/** What a bulk upload returns: what stored, and what did not. */
export interface BulkDocumentUploadResult {
  stored: Array<{ id: string; documentType: string; originalFilename: string }>;
  failed: Array<{ filename: string; reason: string }>;
}

/**
 * Several documents in ONE request, with one note applied to all of them.
 *
 * Deliberately not a loop over the single-file endpoint: that would produce a
 * timeline row and a professional notification PER FILE (spec
 * bulk-document-upload-with-note-as-built).
 *
 * Partial success is normal here, not exceptional — the response names what
 * failed and the caller must surface it rather than assuming everything
 * landed.
 */
export function uploadMyRequestDocumentsBulk(
  requestId: string,
  documentType: string,
  files: File[],
  note?: string | null,
): Promise<BulkDocumentUploadResult> {
  const formData = new FormData();
  formData.append("documentType", documentType);
  if (note != null && note.trim() !== "") {
    formData.append("note", note.trim());
  }
  for (const file of files) {
    formData.append("files", file);
  }
  return apiClient
    .post<BulkDocumentUploadResult>(
      `/me/notarial-requests/${requestId}/documents/bulk`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}
